import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Users, Pencil, Check, X, Plus, AlertTriangle, HelpCircle, ArrowRight, Sparkles, Building2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDate, relTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { SectionTitle, Card, StatusChip } from "@/components/common";
import { ExtractDialog } from "@/components/opportunity/ExtractDialog";
import { MeetingDialog } from "@/components/opportunity/MeetingDialog";
import { ActivityIcon } from "@/components/opportunity/ActivityTab";

function EditableText({ value, onSave, placeholder, multiline = true, testId, className }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value || "");
  const save = async () => {
    await onSave(v);
    setEditing(false);
  };
  if (editing) {
    return (
      <div className="space-y-2">
        {multiline ? <Textarea value={v} onChange={(e) => setV(e.target.value)} rows={4} className="rounded-xl text-sm leading-6" autoFocus data-testid={`${testId}-input`} /> : <Input value={v} onChange={(e) => setV(e.target.value)} autoFocus data-testid={`${testId}-input`} />}
        <div className="flex gap-2"><Button size="sm" onClick={save} className="gap-1 bg-primary text-white hover:bg-[#ff8e76]" data-testid={`${testId}-save`}><Check className="h-3.5 w-3.5" /> Save</Button><Button size="sm" variant="ghost" onClick={() => { setEditing(false); setV(value || ""); }}><X className="h-3.5 w-3.5" /></Button></div>
      </div>
    );
  }
  return (
    <button type="button" onClick={() => { setV(value || ""); setEditing(true); }} data-testid={testId} className={`group w-full text-left ${className || ""}`}>
      {value ? <span className="text-sm leading-6">{value}</span> : <span className="text-sm italic text-muted-foreground">{placeholder}</span>}
      <Pencil className="ml-2 inline h-3 w-3 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
    </button>
  );
}

export function OverviewTab({ data, reload, setTab }) {
  const o = data.opportunity;
  const [extractOpen, setExtractOpen] = useState(false);
  const [meetingOpen, setMeetingOpen] = useState(false);
  const [newTask, setNewTask] = useState("");
  const [newQ, setNewQ] = useState("");

  const patch = async (body) => {
    try {
      await api.patch(`/opportunities/${o.id}`, body);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const openReqs = data.requirements.filter((r) => r.status === "needs_clarification");
  const tasks = data.tasks || [];
  const upcoming = (data.meetings || []).filter((m) => !m.past && m.date >= new Date().toISOString().slice(0, 10)).sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));

  return (
    <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.5fr_1fr]">
      <div className="space-y-9">
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setExtractOpen(true)} data-testid="overview-add-from-email-button"><Mail className="h-3.5 w-3.5" /> Update from e-mail / notes</Button>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setMeetingOpen(true)} data-testid="overview-summarize-meeting-button"><Users className="h-3.5 w-3.5" /> Summarise meeting</Button>
          <Button variant="outline" size="sm" className="gap-1.5" asChild><Link to={`/customers/${o.account_id}?brief=1`} data-testid="overview-prepare-me-link"><Sparkles className="h-3.5 w-3.5 text-primary" /> Prepare me</Link></Button>
        </div>

        <section>
          <SectionTitle>The customer wants</SectionTitle>
          <EditableText value={o.summary} placeholder="Describe what the customer wants - or let the AI fill this from an e-mail or meeting." onSave={(v) => patch({ summary: v })} testId="overview-summary" />
        </section>

        <section>
          <SectionTitle>Next step</SectionTitle>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
            <div className="flex-1"><EditableText value={o.next_step} multiline={false} placeholder="No next step defined - what happens next?" onSave={(v) => patch({ next_step: v })} testId="overview-next-step" /></div>
            <Input type="date" value={o.next_step_date || ""} onChange={(e) => patch({ next_step_date: e.target.value })} className="h-9 w-[160px] rounded-[10px] tabular" data-testid="overview-next-step-date" />
          </div>
        </section>

        <section>
          <SectionTitle right={openReqs.length ? <button type="button" className="text-xs text-primary hover:underline" onClick={() => setTab("requirements")}>Review requirements <ArrowRight className="inline h-3 w-3" /></button> : null}>Open questions</SectionTitle>
          {(o.open_questions || []).length === 0 && openReqs.length === 0 ? <p className="text-sm text-muted-foreground">Nothing open. Everything is clarified.</p> : null}
          <ul className="space-y-1.5">
            {(o.open_questions || []).map((q, i) => (
              <li key={i} className="group flex items-start gap-2 text-sm" data-testid="overview-open-question">
                <HelpCircle className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--status-warn))]" />
                <span className="flex-1">{q}</span>
                <button type="button" title="Resolved" onClick={() => patch({ open_questions: o.open_questions.filter((_, j) => j !== i) })} className="text-muted-foreground opacity-0 transition-opacity hover:text-foreground group-hover:opacity-100" data-testid="overview-resolve-question"><Check className="h-4 w-4" /></button>
              </li>
            ))}
            {openReqs.length > 0 && <li className="text-sm text-muted-foreground">{openReqs.length} requirement{openReqs.length > 1 ? "s" : ""} still need clarification.</li>}
          </ul>
          <form className="mt-2 flex gap-2" onSubmit={(e) => { e.preventDefault(); if (!newQ.trim()) return; patch({ open_questions: [...(o.open_questions || []), newQ.trim()] }); setNewQ(""); }}>
            <Input value={newQ} onChange={(e) => setNewQ(e.target.value)} placeholder="Add a question to clarify..." className="h-9 rounded-[10px]" data-testid="overview-add-question-input" />
            <Button type="submit" variant="outline" size="sm" className="h-9"><Plus className="h-4 w-4" /></Button>
          </form>
        </section>

        <section>
          <SectionTitle>Risks</SectionTitle>
          {(o.risks || []).length === 0 ? <p className="text-sm text-muted-foreground">No risks identified yet.</p> : (
            <ul className="space-y-1.5">
              {o.risks.map((r, i) => (
                <li key={i} className="group flex items-start gap-2 text-sm" data-testid="overview-risk">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--status-warn))]" />
                  <span className="flex-1">{r}</span>
                  <button type="button" title="Remove" onClick={() => patch({ risks: o.risks.filter((_, j) => j !== i) })} className="text-muted-foreground opacity-0 transition-opacity hover:text-foreground group-hover:opacity-100"><X className="h-4 w-4" /></button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section>
          <SectionTitle>AI memory</SectionTitle>
          <div className="grid grid-cols-1 gap-x-8 gap-y-3 text-sm sm:grid-cols-2">
            <div><div className="text-xs text-muted-foreground">Budget</div><EditableText value={o.budget} multiline={false} placeholder="Unknown" onSave={(v) => patch({ budget: v })} testId="overview-budget" /></div>
            <div><div className="text-xs text-muted-foreground">Deadline</div><EditableText value={o.deadline} multiline={false} placeholder="Unknown" onSave={(v) => patch({ deadline: v })} testId="overview-deadline" /></div>
            <div><div className="text-xs text-muted-foreground">Competitors</div><div>{(o.competitors || []).length ? o.competitors.join(", ") : <span className="italic text-muted-foreground">None known</span>}</div></div>
            <div><div className="text-xs text-muted-foreground">Existing equipment</div><div>{(o.existing_equipment || []).length ? o.existing_equipment.join(", ") : <span className="italic text-muted-foreground">Not recorded</span>}</div></div>
            <div className="sm:col-span-2"><div className="text-xs text-muted-foreground">Stakeholders</div><div>{(o.stakeholders || []).length ? o.stakeholders.map((s) => `${s.name} (${s.role})`).join(" · ") : <span className="italic text-muted-foreground">Not recorded</span>}</div></div>
          </div>
        </section>
      </div>

      <aside className="space-y-8">
        <section>
          <SectionTitle right={<Link to={`/customers/${o.account_id}`} className="text-xs text-muted-foreground hover:text-foreground"><Building2 className="inline h-3 w-3" /> {o.account_name}</Link>}>Contacts</SectionTitle>
          {data.contacts.length === 0 ? <p className="text-sm text-muted-foreground">No contacts yet.</p> : (
            <div className="space-y-3">
              {data.contacts.map((c) => (
                <div key={c.id} className="text-sm" data-testid="overview-contact">
                  <div className="font-medium">{c.name}{c.is_decision_maker && <span className="ml-2 rounded-full border px-1.5 py-px text-[10px] text-muted-foreground">Decision maker</span>}</div>
                  <div className="text-muted-foreground">{c.role}</div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section>
          <SectionTitle>Tasks</SectionTitle>
          <div className="space-y-2">
            {tasks.map((t) => (
              <label key={t.id} className="flex items-start gap-2 text-sm" data-testid="overview-task">
                <Checkbox checked={!!t.done} onCheckedChange={async (v) => { await api.patch(`/tasks/${t.id}`, { done: !!v }); reload(); }} className="mt-0.5" />
                <span className={t.done ? "text-muted-foreground line-through" : ""}>{t.title}{t.due ? <span className="ml-1.5 text-xs text-muted-foreground tabular">{fmtDate(t.due)}</span> : null}</span>
              </label>
            ))}
            <form className="flex gap-2" onSubmit={async (e) => { e.preventDefault(); if (!newTask.trim()) return; await api.post(`/opportunities/${o.id}/tasks`, { title: newTask.trim() }); setNewTask(""); reload(); }}>
              <Input value={newTask} onChange={(e) => setNewTask(e.target.value)} placeholder="Add task..." className="h-8 rounded-[10px] text-sm" data-testid="overview-add-task-input" />
              <Button type="submit" size="sm" variant="outline" className="h-8"><Plus className="h-3.5 w-3.5" /></Button>
            </form>
          </div>
        </section>

        {upcoming.length > 0 && (
          <section>
            <SectionTitle>Upcoming meetings</SectionTitle>
            <div className="space-y-2">
              {upcoming.map((m) => (
                <div key={m.id} className="flex items-center gap-3 text-sm"><span className="w-14 shrink-0 text-xs tabular text-muted-foreground">{fmtDate(m.date)}</span><span className="w-11 shrink-0 font-mono text-xs">{m.time}</span><span className="truncate">{m.title}</span></div>
              ))}
            </div>
          </section>
        )}

        <section>
          <SectionTitle right={<button type="button" className="text-xs text-muted-foreground hover:text-foreground" onClick={() => setTab("activity")}>All activity</button>}>Latest activity</SectionTitle>
          <div className="space-y-3">
            {data.activities.slice(0, 6).map((a) => (
              <div key={a.id} className="flex items-start gap-2.5 text-sm" data-testid="overview-activity-item">
                <ActivityIcon type={a.type} className="mt-0.5" />
                <div className="min-w-0 flex-1"><div className="truncate">{a.title}</div><div className="text-xs text-muted-foreground">{relTime(a.date)}</div></div>
              </div>
            ))}
          </div>
        </section>
      </aside>

      <ExtractDialog open={extractOpen} onOpenChange={setExtractOpen} opportunityId={o.id} onDone={reload} />
      <MeetingDialog open={meetingOpen} onOpenChange={setMeetingOpen} opportunityId={o.id} onDone={reload} />
    </div>
  );
}
